/**
 * Where everything sits on the card, at any output size.
 *
 * Nothing here is a pixel literal. Every position and type size derives from a
 * single `unit`, so one layout serves the 4:5 feed post, the 9:16 story and the
 * 1.91:1 link preview without three sets of hand-tuned coordinates drifting apart
 * from each other.
 *
 * There are, however, genuinely **two arrangements**. A portrait card stacks —
 * eyebrow, number, chart, qualification, brand. A landscape card at 1200x630 has
 * barely half the vertical space of the post and nearly the same width, so the
 * same stack scaled down puts the number at a size where the chart has no room
 * left. Landscape therefore reflows into two columns, with the claim on the left
 * and the chart on the right. Scaling a portrait composition into a landscape
 * frame is the thing this file exists to avoid.
 *
 * This module is pure: no canvas, no DOM, no React. Everything below is asserted
 * in `card.test.ts` — containment, non-overlap, and the reflow itself.
 */

/**
 * Canvas font stacks.
 *
 * The full stack, not the bare family name. `ctx.font` takes CSS font shorthand
 * and honours the fallback list, and the webfaces are latin-subsetted — anything
 * outside their `unicode-range` falls through to the system face. Passing only
 * `"Space Grotesk Variable"` would leave the canvas default as the fallback,
 * which differs by platform.
 */
export const FONT_DISPLAY = "'Space Grotesk Variable', ui-sans-serif, system-ui, sans-serif"
export const FONT_UI = "'Inter Variable', ui-sans-serif, system-ui, sans-serif"

/**
 * Space Grotesk's variable weight axis runs 300-700, but `.numeral-hero` in
 * `app.css` sets no `font-weight` at all — so the app's headline renders at 400,
 * and the card has to as well or the two do not match side by side.
 */
export const WEIGHT_HERO = 400
export const WEIGHT_LABEL = 500

export const CARD_SIZES = {
  /** Instagram feed, tallest ratio it allows. Also fine inline on X and Reddit. */
  post: { width: 1080, height: 1350 },
  /** Instagram and WhatsApp stories, full bleed. */
  story: { width: 1080, height: 1920 },
  /** The `og:image` link preview. */
  og: { width: 1200, height: 630 },
} as const

export type CardSizeId = keyof typeof CARD_SIZES
export type Arrangement = 'portrait' | 'landscape'

/**
 * The sizes the share sheet offers, in order. `og` is a build artefact, not a
 * user choice.
 *
 * Declared once because it was declared twice: the hook decided which cards to
 * render and the sheet decided which buttons to draw, from two independent lists,
 * so adding a size to one produced either a permanently disabled button or a card
 * nobody could pick.
 */
export const SHAREABLE_SIZES = ['post', 'story'] as const
export type ShareableSizeId = (typeof SHAREABLE_SIZES)[number]

export const SHARE_LABELS: Record<ShareableSizeId, { title: string; hint: string }> = {
  post: { title: 'Post', hint: 'Feed on Instagram, X and Reddit' },
  story: { title: 'Story', hint: 'Full screen on Instagram and WhatsApp' },
}

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

export interface TextBlock {
  x: number
  y: number
  /** Font size in px, ready for `ctx.font`. */
  size: number
  /** Letter spacing in px. Applied by hand — see `drawTracked` in `paint.ts`. */
  tracking: number
  align: 'left' | 'center'
  /** Wrapping width for multi-line blocks. */
  maxWidth: number
  lineHeight: number
  /**
   * Vertical space this block actually occupies, from `y`.
   *
   * Not derivable from `size` — the hero claim reserves less than its font size
   * because Space Grotesk's cap height sits well inside the em box, while the
   * two-line disclaimer reserves more. The layout is the only thing that knows,
   * so it says so rather than leaving the painter and the tests to guess
   * differently from each other.
   */
  height: number
}

export interface CardLayout {
  width: number
  height: number
  arrangement: Arrangement
  unit: number
  safe: Rect
  eyebrow: TextBlock
  /** The probability, or the tagline when there is no probability to show. */
  claim: TextBlock
  chart: { plot: Rect; axis: TextBlock }
  disclaimer: TextBlock
  brandName: TextBlock
  brandUrl: TextBlock
  strokes: {
    grid: number
    median: number
    medianGlow: number
    requirement: number
    /**
     * Dash pattern for the requirement line.
     *
     * The app uses `strokeDasharray="5 4"`, tuned for a chart about 350 CSS px
     * wide. The card's plot is two to three times that, so a literal 5-and-4
     * would read as a solid line at card scale. Scaling with `unit` is the reason
     * this lives in the layout rather than in the painter.
     */
    requirementDash: readonly [number, number]
    targetRule: number
    markerRadius: number
  }
}

/**
 * Rough text width, for layout guards only.
 *
 * The layout is pure — it has no canvas and therefore no `measureText` — but it
 * still has to reserve space for strings, and getting that wrong is not a subtle
 * failure: an over-long disclaimer runs off the safe area and an under-reserved
 * claim gets drawn on top of the qualification below it. Both happened.
 *
 * 0.52em is an average advance measured across Space Grotesk at mixed case. It is
 * an approximation and must never be used to *place* a glyph — `drawTracked`
 * measures properly for that. Its only job is to let `card.test.ts` assert that a
 * block's text plausibly fits the box reserved for it.
 */
export const AVERAGE_ADVANCE_EM = 0.52

export function estimateTextWidth(text: string, size: number): number {
  return text.length * AVERAGE_ADVANCE_EM * size
}

/** Lines reserved for a claim that can wrap. The percentage never does; the tagline does. */
const CLAIM_LINES_WRAPPED = 3

export function cardLayout(sizeId: CardSizeId): CardLayout {
  const { width, height } = CARD_SIZES[sizeId]
  // 1.4 sits comfortably between the story (0.5625), the post (0.8) and the
  // link preview (1.905), so no size is near the boundary.
  const arrangement: Arrangement = width / height >= 1.4 ? 'landscape' : 'portrait'

  return arrangement === 'landscape'
    ? landscapeLayout(width, height)
    : portraitLayout(width, height)
}

function strokesFor(unit: number): CardLayout['strokes'] {
  return {
    grid: Math.max(1, unit * 0.1),
    median: unit * 0.28,
    medianGlow: unit * 0.95,
    requirement: unit * 0.2,
    requirementDash: [unit * 0.7, unit * 0.55],
    targetRule: Math.max(1, unit * 0.12),
    markerRadius: unit * 0.62,
  }
}

/**
 * Post and story share a width, so `unit` is identical between them and every
 * type size matches. The story's extra 570px of height goes entirely to the
 * chart, which is the only element that benefits from it.
 */
function portraitLayout(width: number, height: number): CardLayout {
  const unit = width / 100
  const pad = unit * 7
  const contentWidth = width - pad * 2

  const eyebrowSize = unit * 2.5
  const claimSize = unit * 17
  // ~27.5px at 1080 wide. There is no numeric minimum in the CCPA Guidelines — the
  // standard is that a normally-sighted person reading once, from a reasonable
  // distance, can see it. The practical version of that here is surviving
  // Instagram's recompression and staying readable at feed-thumbnail scale.
  // The ceiling is the longer disclaimer line fitting `contentWidth` on one line;
  // `card.test.ts` holds both ends of that. Do not shrink it to win layout room.
  const disclaimerSize = unit * 2.45
  const brandNameSize = unit * 3.4
  const brandUrlSize = unit * 2.35
  const axisSize = unit * 2

  const disclaimerLineHeight = disclaimerSize * 1.45
  // Two lines, and both are required — see DISCLAIMER in copy.ts.
  const disclaimerHeight = disclaimerLineHeight * 2

  const eyebrowHeight = eyebrowSize * 1.2
  // Space Grotesk's hero leading in the app is 0.86; the cap height sits well
  // inside that, so the reserved box is deliberately tighter than the font size.
  const claimHeight = claimSize * 0.88
  const brandHeight = brandNameSize * 1.15 + unit * 0.8 + brandUrlSize * 1.2

  const gapAfterEyebrow = unit * 1.4
  const gapAfterClaim = unit * 2.2
  const gapBeforeDisclaimer = unit * 2.6
  const gapBeforeBrand = unit * 2.4

  /*
   * The plot is not the whole chart. "age 45" is drawn above the plot's top edge
   * and the age ticks below its bottom one, so both need reserving — otherwise
   * the label lands on the probability and the ticks land on the disclaimer,
   * which is exactly what the first render did.
   */
  const axisTop = axisSize * 1.7
  const axisBottom = axisSize * 2.2

  const fixed =
    eyebrowHeight +
    gapAfterEyebrow +
    claimHeight +
    gapAfterClaim +
    axisTop +
    axisBottom +
    gapBeforeDisclaimer +
    disclaimerHeight +
    gapBeforeBrand +
    brandHeight

  const chartHeight = height - pad * 2 - fixed

  let y = pad
  const eyebrow = centred(
    y,
    eyebrowSize,
    contentWidth,
    width,
    0.06 * eyebrowSize,
    eyebrowHeight,
  )
  y += eyebrowHeight + gapAfterEyebrow

  const claim = centred(y, claimSize, contentWidth, width, -0.045 * claimSize, claimHeight)
  y += claimHeight + gapAfterClaim + axisTop

  const plot: Rect = { x: pad, y, width: contentWidth, height: chartHeight }
  y += chartHeight + axisBottom + gapBeforeDisclaimer

  const disclaimer = centred(y, disclaimerSize, contentWidth, width, 0, disclaimerHeight)
  disclaimer.lineHeight = disclaimerLineHeight
  y += disclaimerHeight + gapBeforeBrand

  const brandName = centred(
    y,
    brandNameSize,
    contentWidth,
    width,
    -0.02 * brandNameSize,
    brandNameSize * 1.15,
  )
  y += brandNameSize * 1.15 + unit * 0.8

  const brandUrl = centred(
    y,
    brandUrlSize,
    contentWidth,
    width,
    0.02 * brandUrlSize,
    brandUrlSize * 1.2,
  )

  return {
    width,
    height,
    arrangement: 'portrait',
    unit,
    safe: { x: pad, y: pad, width: contentWidth, height: height - pad * 2 },
    eyebrow,
    claim,
    chart: { plot, axis: centred(0, axisSize, contentWidth, width, 0, axisBottom) },
    disclaimer,
    brandName,
    brandUrl,
    strokes: strokesFor(unit),
  }
}

/**
 * The link preview. Claim on the left, chart on the right, qualification and
 * brand along the bottom.
 *
 * `unit` comes from the height here, not the width — 1200x630 is short and wide,
 * and sizing type off the width would produce a probability taller than the space
 * available for it.
 */
function landscapeLayout(width: number, height: number): CardLayout {
  const unit = height / 100
  const pad = unit * 8
  const contentWidth = width - pad * 2

  const columnGap = unit * 5
  const leftWidth = contentWidth * 0.46
  const rightX = pad + leftWidth + columnGap
  const rightWidth = width - pad - rightX

  const eyebrowSize = unit * 3.2
  /*
   * Smaller than the portrait claim relative to its card, because in landscape
   * the claim is usually the TAGLINE and the tagline wraps. At unit*13 it wrapped
   * to three lines that ran straight through the disclaimer below — the reserved
   * box assumed one line, which is true of "70%" and false of a sentence.
   */
  const claimSize = unit * 10
  const disclaimerSize = unit * 3
  const brandNameSize = unit * 4.4
  const brandUrlSize = unit * 2.9
  const axisSize = unit * 2.6

  const disclaimerLineHeight = disclaimerSize * 1.4
  const disclaimerHeight = disclaimerLineHeight * 2
  const bottomStripHeight = Math.max(disclaimerHeight, brandNameSize * 2.4)
  const bottomY = height - pad - bottomStripHeight

  const axisTop = axisSize * 1.7
  const axisBottom = axisSize * 2.2
  const bodyHeight = bottomY - pad - unit * 2

  // The left column is centred against the chart rather than pinned to the top,
  // so the two columns read as one composition.
  const claimHeight = claimSize * 1.05 * CLAIM_LINES_WRAPPED
  const eyebrowHeight = eyebrowSize * 1.2
  const gapAfterEyebrow = unit * 1.6
  const leftStackHeight = eyebrowHeight + gapAfterEyebrow + claimHeight
  const leftTop = pad + Math.max(0, (bodyHeight - leftStackHeight) / 2)

  const eyebrow = leftAligned(
    pad,
    leftTop,
    eyebrowSize,
    leftWidth,
    0.06 * eyebrowSize,
    eyebrowHeight,
  )
  const claim = leftAligned(
    pad,
    leftTop + eyebrowHeight + gapAfterEyebrow,
    claimSize,
    leftWidth,
    -0.045 * claimSize,
    claimHeight,
  )

  const plot: Rect = {
    x: rightX,
    y: pad + axisTop,
    width: rightWidth,
    height: bodyHeight - axisTop - axisBottom,
  }

  const disclaimer = leftAligned(
    pad,
    bottomY + (bottomStripHeight - disclaimerHeight) / 2,
    disclaimerSize,
    contentWidth * 0.62,
    0,
    disclaimerHeight,
  )
  disclaimer.lineHeight = disclaimerLineHeight

  const brandBlockHeight = brandNameSize * 1.15 + unit * 0.6 + brandUrlSize * 1.2
  const brandTop = bottomY + (bottomStripHeight - brandBlockHeight) / 2
  const brandLeft = pad + contentWidth * 0.68
  const brandWidth = contentWidth * 0.32

  const brandName = leftAligned(
    brandLeft,
    brandTop,
    brandNameSize,
    brandWidth,
    -0.02 * brandNameSize,
    brandNameSize * 1.15,
  )
  const brandUrl = leftAligned(
    brandLeft,
    brandTop + brandNameSize * 1.15 + unit * 0.6,
    brandUrlSize,
    brandWidth,
    0.02 * brandUrlSize,
    brandUrlSize * 1.2,
  )

  return {
    width,
    height,
    arrangement: 'landscape',
    unit,
    safe: { x: pad, y: pad, width: contentWidth, height: height - pad * 2 },
    eyebrow,
    claim,
    chart: { plot, axis: leftAligned(rightX, 0, axisSize, rightWidth, 0, axisBottom) },
    disclaimer,
    brandName,
    brandUrl,
    strokes: strokesFor(unit),
  }
}

function centred(
  y: number,
  size: number,
  maxWidth: number,
  width: number,
  tracking: number,
  height: number,
): TextBlock {
  return {
    x: width / 2,
    y,
    size,
    tracking,
    align: 'center',
    maxWidth,
    lineHeight: size * 1.3,
    height,
  }
}

function leftAligned(
  x: number,
  y: number,
  size: number,
  maxWidth: number,
  tracking: number,
  height: number,
): TextBlock {
  return {
    x,
    y,
    size,
    tracking,
    align: 'left',
    maxWidth,
    lineHeight: size * 1.3,
    height,
  }
}
